import { useEffect, useState, useRef } from 'react';
import { ApiClient, type BookManifestItem, type PlaylistTrack, FALLBACK_BOOKS } from './services/ApiClient';
import { LibraryDB } from './services/LibraryDB';
import { downloadManager, type DownloadStatus } from './services/DownloadManager';
import { offlineStore } from './services/OfflineStore';
import { CorpusService } from './services/CorpusRegistry';
import { GutendexService, type GutendexBook } from './services/Gutendex';
import { loadSettings, saveSettings, type VoiceSettings } from './settings/voiceSettings';
import { useTtsAutoAdvance } from './hooks/useTtsAutoAdvance';
import { stopSpeaking } from './utils/tts';
import { saveLastRead, loadLastRead } from './utils/smartReader';
import { PublicDomainCorpus } from './services/PublicDomainCorpus';
import { cleanGutenbergText } from './utils/gutenbergCleaner';

// Helper to resolve API URLs
const resolveUrl = (url?: string): string => {
  if (!url) return '';
  if (url.startsWith('/api/')) {
    return `https://corpora.wikiboox.com${url}`;
  }
  return url;
};

// Helper to determine if a book/section has explicit chapter titles
const hasExplicitChapters = (_bookId: string, corpusId: string, chapterTitles?: string[]): boolean => {
  if (corpusId === 'siddhartha') return true;
  if (chapterTitles && chapterTitles.length > 0) {
    if (chapterTitles.length === 1) {
      const first = chapterTitles[0].trim();
      if (first === 'Page 1' || first === 'Chapter 1') {
        return false;
      }
    }
    const allPagesOrSegments = chapterTitles.every(t => {
      const norm = t.trim().toLowerCase();
      return norm.startsWith('page ') || norm.startsWith('segment ');
    });
    if (allPagesOrSegments) {
      return false;
    }
    return true;
  }
  return false;
};

// Helper to determine reader navigation labels, center title, and accessibility labels
const getReaderNavigationLabels = (
  bookId: string,
  corpusId: string,
  currentChapter: number,
  maxChapters: number,
  chapterTitles?: string[]
) => {
  const isExplicit = hasExplicitChapters(bookId, corpusId, chapterTitles);
  
  if (isExplicit) {
    const title = chapterTitles?.[currentChapter - 1];
    return {
      prevLabel: "← Prev Chapter",
      nextLabel: "Next Chapter →",
      centerLabel: title ? `Chapter ${currentChapter}: ${title}` : `Chapter ${currentChapter}`,
      prevAria: "Previous chapter",
      nextAria: "Next chapter"
    };
  } else {
    const maxText = maxChapters > 0 ? ` of ${maxChapters}` : "";
    return {
      prevLabel: "← Previous",
      nextLabel: "Next →",
      centerLabel: `Page ${currentChapter}${maxText}`,
      prevAria: "Previous page",
      nextAria: "Next page"
    };
  }
};

export default function App() {
  // Navigation: 'onboarding' | 'library' | 'discover' | 'reader' | 'player' | 'settings'
  const [view, setView] = useState<'onboarding' | 'library' | 'detail' | 'discover' | 'reader' | 'player' | 'settings'>('onboarding');
  
  // Curated catalog books
  const [books, setBooks] = useState<BookManifestItem[]>([]);
  const [selectedBook, setSelectedBook] = useState<BookManifestItem | null>(null);
  
  // App-level state
  const [isPro, setIsPro] = useState<boolean>(false);
  const [isDemoFallback, setIsDemoFallback] = useState<boolean>(false);
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [toast, setToast] = useState<string | null>(null);
  
  // Download states for premium audio
  const [downloads, setDownloads] = useState<Record<string, DownloadStatus>>({});
  const [offlineStatus, setOfflineStatus] = useState<Record<string, boolean>>({});

  // Audio Playback Engine State (for MP3 Narrated tracks)
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [activeBookId, setActiveBookId] = useState<string | null>(null);
  const [tracks, setTracks] = useState<PlaylistTrack[]>([]);
  const [currentTrackIndex, setCurrentTrackIndex] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1.0);
  const [audioLoading, setAudioLoading] = useState<boolean>(false);
  const [audioError, setAudioError] = useState<string | null>(null);

  // Resume state for narrated tracks
  const [resumeCandidate, setResumeCandidate] = useState<{ trackIndex: number; position: number } | null>(null);
  const [showResumePrompt, setShowResumePrompt] = useState<boolean>(false);

  // Sleep Timer state
  const [sleepTimerEndAt, setSleepTimerEndAt] = useState<number | null>(null);
  const [showSleepPicker, setShowSleepPicker] = useState<boolean>(false);
  const [, setSleepTimerTick] = useState<number>(0);
  const [sleepAtEndOfTrack, setSleepAtEndOfTrack] = useState<boolean>(() => {
    return localStorage.getItem('audio_sleep_at_end_of_track') === 'true';
  });

  // Upsell state
  const [showUpsell, setShowUpsell] = useState<boolean>(false);

  // --- TRANSPLANTED WORK (Phase 2 & Phase 3 States) ---
  
  // Local/Downloaded shelf (combination of downloaded Gutenberg books and Siddhartha)
  const [localBooks, setLocalBooks] = useState<any[]>([]);
  
  // Discover Tab Gutendex Search
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<GutendexBook[]>([]);
  const [searching, setSearching] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [downloadingBookIds, setDownloadingBookIds] = useState<Set<string>>(new Set());

  // Reader Tab Pagination & Text State
  const [readerBook, setReaderBook] = useState<{
    id: string;
    title: string;
    author: string;
    corpusId: string;
  } | null>(null);
  const [currentChapter, setCurrentChapter] = useState<number>(1);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [verseData, setVerseData] = useState<Record<string, string> | null>(null);
  const [pages, setPages] = useState<string[][]>([]);
  const [isPaginating, setIsPaginating] = useState<boolean>(false);
  const [pendingPageRestore, setPendingPageRestore] = useState<number | null>(null);
  const [autoStartTts, setAutoStartTts] = useState<boolean>(false);
  const [downloadingTextIds, setDownloadingTextIds] = useState<Set<string>>(new Set());
  const [showPlaybackErrorModal, setShowPlaybackErrorModal] = useState<boolean>(false);
  const [playbackErrorBook, setPlaybackErrorBook] = useState<BookManifestItem | null>(null);

  // Web Speech TTS Synthesis States
  const [isSpeaking, setIsSpeaking] = useState<boolean>(false);
  const [isBuffering, setIsBuffering] = useState<boolean>(false);
  const [voiceSettings, setVoiceSettings] = useState<VoiceSettings>(loadSettings());
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);

  // Navigation track
  const lastManualNavAtRef = useRef<number>(0);
  const readerStateRef = useRef({ pageIndex, pages, currentChapter, readerBook });

  // Show a toast message helper
  const triggerToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  // --- TRANSPLANTED WORK EVENT HANDLERS ---
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setSearchError(null);
    try {
      const res = await GutendexService.search(searchQuery.trim());
      setSearchResults(res.results || []);
    } catch (err: any) {
      console.error('Search failed:', err);
      setSearchError(err?.message || 'Failed to search Gutenberg library.');
    } finally {
      setSearching(false);
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleVoiceChange = (voiceURI: string) => {
    updateVoiceSetting(s => {
      s.free.voiceURI = voiceURI;
    });
  };

  const handleRateChange = (rate: number) => {
    updateVoiceSetting(s => {
      s.free.rate = rate;
    });
  };

  const handlePitchChange = (pitch: number) => {
    updateVoiceSetting(s => {
      s.free.pitch = pitch;
    });
  };

  const handleAutoPageFlipChange = (checked: boolean) => {
    updateVoiceSetting(s => {
      s.autoPageFlip = checked;
    });
  };

  // 1. Initial Load & Sync
  useEffect(() => {
    // Load theme
    const savedTheme = localStorage.getItem('claranarratio_theme');
    if (savedTheme === 'dark') setTheme('dark');

    // Read sleep timer from localStorage
    const savedSleepTimer = localStorage.getItem('audio_sleep_timer_endAt');
    if (savedSleepTimer) {
      const endAt = parseInt(savedSleepTimer, 10);
      if (endAt > Date.now()) {
        setSleepTimerEndAt(endAt);
      } else {
        localStorage.removeItem('audio_sleep_timer_endAt');
      }
    }

    // Load offline books from DB first
    let hasLoadedFromCache = false;
    LibraryDB.getAudioManifest()
      .then(items => {
        if (items && items.length > 0) {
          setBooks(items);
          hasLoadedFromCache = true;
          setView('library');
        }
      })
      .catch(err => console.warn("Failed to load manifest from cache:", err))
      .finally(() => {
        // Fetch fresh manifest & entitlement
        Promise.all([
          ApiClient.fetchManifest(),
          ApiClient.fetchEntitlement()
        ]).then(([manifest, entitlement]) => {
          setIsPro(entitlement.isPro);
          if (manifest && manifest.length > 0) {
            setBooks(manifest);
            setIsDemoFallback(false);
            LibraryDB.saveAudioManifest(manifest).catch(err => console.error("IDB manifest save error:", err));
          } else if (!hasLoadedFromCache) {
            setBooks(FALLBACK_BOOKS);
            setIsDemoFallback(true);
          }
        }).catch(err => {
          console.warn("Offline fallback: fetch failed:", err);
          if (!hasLoadedFromCache) {
            setBooks(FALLBACK_BOOKS);
            setIsDemoFallback(true);
          }
        });
      });

    // Check which books are offline on mount
    const updateOfflineStatuses = async () => {
      const statuses: Record<string, boolean> = {};
      const manifest = await LibraryDB.getAudioManifest() || [];
      for (const item of manifest) {
        statuses[item.id] = await offlineStore.hasBookOffline(item.id);
      }
      setOfflineStatus(statuses);
    };
    updateOfflineStatuses();

    // Initialize Corpora & Local Books
    CorpusService.init().then(() => {
      loadLocalBooks();
    });
  }, []);

  // Sync theme to body element
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('claranarratio_theme', theme);
  }, [theme]);

  // Sync downloadManager listener
  useEffect(() => {
    const handleDownloadStatus = (status: DownloadStatus) => {
      setDownloads(prev => ({ ...prev, [status.bookId]: status }));
      
      // Update overall offline status if completed or removed
      if (status.state === 'completed') {
        setOfflineStatus(prev => ({ ...prev, [status.bookId]: true }));
        triggerToast("Download completed successfully.");
        loadLocalBooks();
      } else if (status.state === 'idle' && status.progress === 0) {
        setOfflineStatus(prev => ({ ...prev, [status.bookId]: false }));
        loadLocalBooks();
      }
    };

    downloadManager.addListener(handleDownloadStatus);
    return () => downloadManager.removeListener(handleDownloadStatus);
  }, []);

  // Sleep timer interval ticks
  useEffect(() => {
    if (!sleepTimerEndAt) return;
    const interval = setInterval(() => {
      setSleepTimerTick(t => t + 1);
      if (Date.now() >= sleepTimerEndAt) {
        if (audioRef.current) {
          audioRef.current.pause();
          setIsPlaying(false);
        }
        if (isSpeaking) {
          stopSpeaking('sleep_timer');
          setIsSpeaking(false);
        }
        setSleepTimerEndAt(null);
        localStorage.removeItem('audio_sleep_timer_endAt');
        triggerToast("Sleep timer finished. Playback paused.");
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [sleepTimerEndAt, isSpeaking]);

  // Sync audio source when track changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || tracks.length === 0) return;

    const currentTrack = tracks[currentTrackIndex];
    if (!currentTrack || !currentTrack.url) return;

    const playTrack = async () => {
      setAudioLoading(true);
      setAudioError(null);
      
      try {
        // Resolve audio source (check offline OPFS first, fallback to network)
        let audioBlobUrl = '';
        if (activeBookId) {
          const filename = currentTrack.key ? currentTrack.key.split('/').pop() || 'audio.mp3' : 'audio.mp3';
          const cachedBlob = await offlineStore.getChunk(activeBookId, filename);
          if (cachedBlob) {
            audioBlobUrl = URL.createObjectURL(cachedBlob);
            console.log(`[Player] Loaded offline chunk: ${filename}`);
          }
        }

        audio.src = audioBlobUrl || resolveUrl(currentTrack.url);
        audio.playbackRate = playbackSpeed;
        
        // Wait for metadata to load
        await new Promise<void>((resolve, reject) => {
          const handleLoaded = () => {
            audio.removeEventListener('loadedmetadata', handleLoaded);
            audio.removeEventListener('error', handleError);
            resolve();
          };
          const handleError = (_e: any) => {
            audio.removeEventListener('loadedmetadata', handleLoaded);
            audio.removeEventListener('error', handleError);
            reject(new Error("Playback source load error"));
          };
          audio.addEventListener('loadedmetadata', handleLoaded);
          audio.addEventListener('error', handleError);
        });

        setAudioLoading(false);

        // Apply resume seek position if pending
        if (resumeCandidate && resumeCandidate.trackIndex === currentTrackIndex) {
          audio.currentTime = resumeCandidate.position;
          setResumeCandidate(null);
        }

        if (isPlaying) {
          audio.play().catch(err => {
            console.warn("Autoplay blocked:", err);
            setIsPlaying(false);
          });
        }
      } catch (err: any) {
        console.error("Audio playback error:", err);
        setAudioError("Unable to load audio track.");
        setAudioLoading(false);
        setIsPlaying(false);
      }
    };

    playTrack();
  }, [currentTrackIndex, tracks]);

  // Sync playbackSpeed
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed]);

  // Load browser TTS voices
  useEffect(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    const fetchVoices = () => {
      setVoices(window.speechSynthesis.getVoices());
    };
    fetchVoices();
    window.speechSynthesis.onvoiceschanged = fetchVoices;
    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
  }, []);

  // --- TRANSPLANTED LOGIC METHODS (Phase 2 & Phase 3) ---

  const loadLocalBooks = async () => {
    try {
      const dbBooks = await LibraryDB.getAllBooks();
      const readyBooks = dbBooks.filter(b => b.status === 'ready');
      
      const combined: any[] = [...readyBooks];
      const sidCorpus = CorpusService.get('siddhartha');
      if (sidCorpus) {
        combined.push({
          id: 'siddhartha',
          title: 'Siddhartha',
          authors: [{ name: 'Hermann Hesse' }],
          coverUrl: undefined,
          corpusId: 'siddhartha'
        });
      }
      setLocalBooks(combined);
    } catch (err) {
      console.warn("Failed to load local books shelf:", err);
    }
  };

  const handleDownloadGutenberg = async (book: GutendexBook) => {
    const bookIdStr = book.id.toString();
    if (downloadingBookIds.has(bookIdStr)) return;

    setDownloadingBookIds(prev => {
      const next = new Set(prev);
      next.add(bookIdStr);
      return next;
    });

    try {
      const corpus = CorpusService.get('public_domain_gutendex') as PublicDomainCorpus;
      if (!corpus) throw new Error("Public Domain corpus not found");
      
      triggerToast(`Downloading ${book.title}...`);
      await corpus.downloadBook(book);
      await corpus.load();
      await loadLocalBooks();
      triggerToast(`Downloaded ${book.title} successfully!`);
    } catch (err: any) {
      console.error("Gutenberg download failed:", err);
      alert("Download failed: " + err.message);
    } finally {
      setDownloadingBookIds(prev => {
        const next = new Set(prev);
        next.delete(bookIdStr);
        return next;
      });
    }
  };

  const handleDeleteBook = async (bookId: string, title: string) => {
    if (bookId === 'siddhartha') {
      triggerToast("Cannot delete Siddhartha.");
      return;
    }
    if (confirm(`Remove "${title}" from your downloaded library?`)) {
      try {
        await LibraryDB.deleteBook(bookId);
        const corpus = CorpusService.get('public_domain_gutendex') as PublicDomainCorpus;
        if (corpus) {
          await corpus.load();
        }
        await loadLocalBooks();
        triggerToast(`Removed "${title}".`);
      } catch (err) {
        console.error("Failed to delete book:", err);
        triggerToast("Failed to delete book.");
      }
    }
  };

  const downloadCuratedBookText = async (id: string, title: string, author: string): Promise<boolean> => {
    try {
      const url = resolveUrl(`/api/library/source/${id}`);
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error(`Failed to fetch source text: ${res.status}`);
      }
      const rawText = await res.text();
      const cleanText = cleanGutenbergText(rawText);

      await LibraryDB.addBook({
        id: id,
        title: title,
        authors: [{ name: author }],
        languages: ['en'],
        addedAt: Date.now(),
        formats: {},
        status: 'ready',
        textContent: cleanText,
        chunkedChapters: [cleanText]
      });

      const corpus = CorpusService.get('public_domain_gutendex') as PublicDomainCorpus;
      if (corpus) {
        await corpus.load();
      }
      return true;
    } catch (e) {
      console.error("Failed to download curated book text:", e);
      return false;
    }
  };

  const handleReadCuratedBook = async (book: BookManifestItem, startTts: boolean = false) => {
    const existing = await LibraryDB.getBook(book.id);
    if (existing && existing.status === 'ready' && existing.chunkedChapters && existing.chunkedChapters.length > 0) {
      handleSelectBookForReading(book.id, book.title, book.author, 'public_domain_gutendex');
      if (startTts) {
        setAutoStartTts(true);
      }
      return;
    }

    setDownloadingTextIds(prev => {
      const next = new Set(prev);
      next.add(book.id);
      return next;
    });
    triggerToast(`Downloading book text...`);

    const success = await downloadCuratedBookText(book.id, book.title, book.author);
    setDownloadingTextIds(prev => {
      const next = new Set(prev);
      next.delete(book.id);
      return next;
    });

    if (success) {
      handleSelectBookForReading(book.id, book.title, book.author, 'public_domain_gutendex');
      if (startTts) {
        setAutoStartTts(true);
      }
    } else {
      triggerToast("Unable to download text content.");
    }
  };

  // Select Gutenberg or Local book for reading in the Reader tab
  const handleSelectBookForReading = (id: string, title: string, author: string, corpusId: string) => {
    // Safely pause MP3 audio playback first to prevent sound collision
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }

    setReaderBook({ id, title, author, corpusId });
    setView('reader');

    const saved = loadLastRead(corpusId, id);
    if (saved) {
      setCurrentChapter(saved.chapter);
      setPageIndex(saved.pageIndex || 0);
    } else {
      setCurrentChapter(1);
      setPageIndex(0);
    }
  };

  // Triggers TTS + Opens Reader view from local shelf
  const handleListenLocalBook = (book: any) => {
    const bookAudio = books.find(b => b.id === book.id);
    if (bookAudio && bookAudio.hasAudio) {
      handleStartPlayback(bookAudio);
      setView('player');
    } else {
      handleSelectBookForReading(book.id, book.title, book.authors?.[0]?.name || 'Unknown Author', book.corpusId || 'public_domain_gutendex');
      setAutoStartTts(true);
    }
  };

  // Reader Pagination: Split chapter paragraph map into 4-paragraph pages
  useEffect(() => {
    if (!verseData) {
      setPages([]);
      return;
    }
    const keys = Object.keys(verseData);
    if (keys.length === 0) {
      setPages([[]]);
      return;
    }
    const newPages: string[][] = [];
    const CHUNK_SIZE = 4;
    for (let i = 0; i < keys.length; i += CHUNK_SIZE) {
      newPages.push(keys.slice(i, i + CHUNK_SIZE));
    }
    setPages(newPages);
  }, [verseData]);

  // Load Chapter contents dynamically when Reader selection or chapter changes
  useEffect(() => {
    if (!readerBook) return;

    let active = true;
    setIsPaginating(true);
    
    const loadChapter = async () => {
      try {
        const corpus = CorpusService.get(readerBook.corpusId);
        if (!corpus) throw new Error("Corpus registry item not found");
        
        await corpus.load();
        const data = corpus.getChapter(readerBook.id, currentChapter);
        if (!active) return;

        if (data && Object.keys(data).length > 0) {
          setVerseData(data);
        } else {
          console.warn("Empty chapter data returned", readerBook.id, currentChapter);
          setVerseData({});
        }
      } catch (err) {
        console.error("Failed to load chapter text:", err);
        if (active) setVerseData({});
      } finally {
        if (active) setIsPaginating(false);
      }
    };

    loadChapter();
    return () => { active = false; };
  }, [readerBook, currentChapter]);

  // Restore pageIndex if we navigated back to previous chapter (sets to last page)
  useEffect(() => {
    if (pages.length > 0 && pendingPageRestore !== null) {
      if (pendingPageRestore === -1) {
        setPageIndex(pages.length - 1);
      } else if (pendingPageRestore < pages.length) {
        setPageIndex(pendingPageRestore);
      }
      setPendingPageRestore(null);
    }
  }, [pages, pendingPageRestore]);

  // Sync readerStateRef and handle reading progress save
  useEffect(() => {
    readerStateRef.current = { pageIndex, pages, currentChapter, readerBook };

    if (readerBook) {
      saveLastRead({
        corpusId: readerBook.corpusId,
        bookId: readerBook.id,
        chapter: currentChapter,
        pageIndex: pageIndex,
        timestamp: Date.now()
      });
    }

    // Safety Guard: Stop TTS synthesis if we recently changed pages manually
    if (isSpeaking) {
      const timeSinceNav = Date.now() - lastManualNavAtRef.current;
      if (timeSinceNav < 500) {
        console.log("[App] Safety Guard: Stopping TTS after manual nav (race detected)");
        stopSpeaking('manual_nav_guard');
        setIsSpeaking(false);
      }
    }
  }, [pageIndex, pages, currentChapter, readerBook, isSpeaking]);

  // Helper for next chapter/page logic (used by TTS and UI)
  const advancePageOrChapter = (reason: 'auto' | 'manual'): boolean => {
    const { pageIndex, pages, currentChapter, readerBook } = readerStateRef.current;

    if (reason === 'manual') {
      lastManualNavAtRef.current = Date.now();
    }

    if (pageIndex < pages.length - 1) {
      setPageIndex(p => p + 1);
      return true;
    } else {
      // Next Chapter Logic
      if (!readerBook) return false;
      const corpus = CorpusService.get(readerBook.corpusId);
      if (!corpus) return false;
      const sections = corpus.getSections();
      const section = sections.find(s => s.id === readerBook.id || (readerBook.corpusId === 'siddhartha' && s.id === 'main'));
      const maxCh = section?.chapterCount || 0;

      const nextCh = currentChapter + 1;
      if (nextCh <= maxCh) {
        setVerseData(null);
        setCurrentChapter(nextCh);
        setPageIndex(0);
        return true;
      }
      return false; // End of book
    }
  };

  // Auto-Advance Hook Setup for Browser TTS
  const tts = useTtsAutoAdvance({
    isPlaying: isSpeaking,
    setIsPlaying: setIsSpeaking,
    pageIndex,
    pages,
    verseData,
    isPaginating,
    advancePageOrChapter,
    onToast: triggerToast,
    setIsBuffering,
    bookId: readerBook?.id || '',
    chapter: currentChapter,
    corpusId: readerBook?.corpusId || ''
  });

  const handleTtsToggle = () => {
    if (!verseData || pages.length === 0 || !pages[pageIndex]) return;
    const verses = pages[pageIndex].map(v => verseData[v] || '');
    if (verses.length === 0) return;

    // Safely pause premium MP3 audio first to prevent overlap
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }

    if (isSpeaking) {
      tts.stop('user_toggle');
    } else {
      tts.play(verses);
    }
  };

  // Trigger auto start TTS if set
  useEffect(() => {
    if (autoStartTts && view === 'reader' && verseData && pages.length > 0 && pages[pageIndex]) {
      setAutoStartTts(false);
      const verses = pages[pageIndex].map(v => verseData[v] || '');
      if (verses.length > 0) {
        // Pause premium MP3 audio
        if (isPlaying && audioRef.current) {
          audioRef.current.pause();
          setIsPlaying(false);
        }
        tts.play(verses);
      }
    }
  }, [autoStartTts, view, verseData, pages, pageIndex]);

  // Clean stop TTS when unmounting
  useEffect(() => {
    return () => {
      if (isSpeaking) {
        stopSpeaking('unmount');
      }
    };
  }, [isSpeaking]);

  const handlePrev = () => {
    lastManualNavAtRef.current = Date.now();
    if (isSpeaking) tts.stop('manual_page_prev');

    if (pageIndex > 0) {
      setPageIndex(p => p - 1);
    } else if (currentChapter > 1) {
      setVerseData(null);
      setCurrentChapter(c => c - 1);
      setPendingPageRestore(-1);
    }
  };

  const handleNext = () => {
    lastManualNavAtRef.current = Date.now();
    if (isSpeaking) tts.stop('manual_page_next');
    
    const success = advancePageOrChapter('manual');
    if (!success) {
      triggerToast("End of book.");
    }
  };

  // Safe Click Zones for Reader Navigation (35% Left back, 65% Right next)
  const handlePageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('select') || target.closest('a')) {
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const ratio = clickX / rect.width;
    if (ratio < 0.35) {
      handlePrev();
    } else {
      handleNext();
    }
  };

  const updateVoiceSetting = (updater: (s: VoiceSettings) => void) => {
    setVoiceSettings(prev => {
      const next = { ...prev };
      updater(next);
      saveSettings(next);
      return next;
    });
  };

  // Keyboard navigation for Reader
  useEffect(() => {
    if (view !== 'reader') return;

    const handleKey = (e: KeyboardEvent) => {
      const active = document.activeElement;
      if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || (active as HTMLElement).isContentEditable)) return;

      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrev();
      }
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [view, pageIndex, currentChapter, pages]);

  // --- PRE-EXISTING NARRATED AUDIO PLAYER ENGINE METHODS ---

  const handleBookClick = async (book: BookManifestItem) => {
    setSelectedBook(book);
    setView('detail');

    // Query download status
    const status = await downloadManager.getStatus(book.id);
    setDownloads(prev => ({ ...prev, [book.id]: status }));
  };

  const handleStartPlayback = async (book: BookManifestItem) => {
    // Safely pause browser-native TTS synthesis
    if (isSpeaking) {
      tts.stop('audio_playback_started');
      setIsSpeaking(false);
    }

    setAudioLoading(true);
    setAudioError(null);
    setTracks([]);
    setActiveBookId(book.id);
    setCurrentTrackIndex(0);
    setResumeCandidate(null);
    setShowResumePrompt(false);
    setIsPlaying(false);

    try {
      // 1. Check offline DB for cached playlist.json
      let playlistData: any = null;
      const cachedPlaylistBlob = await offlineStore.getChunk(book.id, 'playlist.json');
      if (cachedPlaylistBlob) {
        const text = await cachedPlaylistBlob.text();
        playlistData = JSON.parse(text);
        console.log("[Player] Loaded offline playlist.json");
      } else {
        // Fetch from network
        playlistData = await ApiClient.fetchPlaylist(book.id, resolveUrl(book.playlistUrl));
      }

      let parsedTracks: any[] = [];
      if (Array.isArray(playlistData)) parsedTracks = playlistData;
      else if (playlistData && Array.isArray(playlistData.tracks)) parsedTracks = playlistData.tracks;
      else if (playlistData && Array.isArray(playlistData.items)) parsedTracks = playlistData.items;

      const validTracks = parsedTracks.map((t, i) => ({
        index: i,
        url: typeof t === 'string' ? t : (t.url || t.src || t.uri),
        key: typeof t === 'string' ? t : (t.key || t.id)
      })).filter(t => !!t.url);

      if (validTracks.length === 0) {
        throw new Error("No playable audio tracks found.");
      }

      // PRE-FLIGHT CHECK ON FIRST TRACK
      const firstTrackUrl = resolveUrl(validTracks[0].url);
      console.log(`[Player] Pre-flight check on track 0: ${firstTrackUrl}`);
      let available = false;
      try {
        const resHead = await fetch(firstTrackUrl, { method: 'HEAD' });
        if (resHead.ok) {
          available = true;
        } else {
          // Fall back to Range request
          const resGet = await fetch(firstTrackUrl, {
            headers: { 'Range': 'bytes=0-0' }
          });
          if (resGet.status === 200 || resGet.status === 206) {
            available = true;
          }
        }
      } catch (err) {
        console.warn("[Player] Pre-flight track check failed:", err);
      }

      if (!available) {
        throw new Error(`First track is not accessible: ${firstTrackUrl}`);
      }

      setTracks(validTracks);

      // Check if there is an audio resume point in IndexedDB
      const resume = await LibraryDB.getAudioResume(book.id);
      if (resume && (resume.trackIndex > 0 || resume.positionSeconds > 10)) {
        setResumeCandidate({ trackIndex: resume.trackIndex, position: resume.positionSeconds });
        setShowResumePrompt(true);
        setIsPlaying(false);
      } else {
        setIsPlaying(true);
      }
    } catch (err: any) {
      console.error("[Player] Playback check failed:", err);
      setTracks([]);
      setActiveBookId(null);
      setIsPlaying(false);
      setAudioLoading(false);
      
      setPlaybackErrorBook(book);
      setShowPlaybackErrorModal(true);
    }
  };

  const applyResume = () => {
    if (resumeCandidate) {
      setCurrentTrackIndex(resumeCandidate.trackIndex);
      setIsPlaying(true);
      if (audioRef.current) {
        audioRef.current.currentTime = resumeCandidate.position;
      }
      triggerToast("Playback position resumed.");
    }
    setShowResumePrompt(false);
  };

  const declineResume = () => {
    setResumeCandidate(null);
    setShowResumePrompt(false);
    setIsPlaying(true);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  };

  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio) return;

    setCurrentTime(audio.currentTime);
    setDuration(audio.duration || 0);

    // 2. Non-member limit cutoff check: 5 minutes preview on track 0, or index > 0.
    if (!isPro) {
      if (currentTrackIndex > 0 || audio.currentTime > 300) {
        audio.pause();
        setIsPlaying(false);
        setShowUpsell(true);
      }
    }

    // Save resume position (throttled)
    if (activeBookId) {
      LibraryDB.saveAudioResume({
        bookId: activeBookId,
        trackIndex: currentTrackIndex,
        positionSeconds: audio.currentTime,
        updatedAt: Date.now(),
        durationSeconds: audio.duration || undefined
      }).catch(err => console.error("Failed to save resume state:", err));
    }
  };

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      // Pause TTS if speaking
      if (isSpeaking) {
        tts.stop('audio_playback_started');
        setIsSpeaking(false);
      }
      audio.play().then(() => {
        setIsPlaying(true);
      }).catch(err => {
        console.warn("Audio play failed:", err);
      });
    }
  };

  const handleSeek = (time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const skipSeconds = (secs: number) => {
    if (audioRef.current) {
      let nextTime = audioRef.current.currentTime + secs;
      if (nextTime < 0) nextTime = 0;
      if (nextTime > duration) nextTime = duration;
      audioRef.current.currentTime = nextTime;
      setCurrentTime(nextTime);
    }
  };

  const nextTrack = () => {
    if (currentTrackIndex < tracks.length - 1) {
      setCurrentTrackIndex(prev => prev + 1);
    } else {
      triggerToast("End of book.");
    }
  };

  const prevTrack = () => {
    if (currentTrackIndex > 0) {
      setCurrentTrackIndex(prev => prev - 1);
    }
  };

  const formatTime = (seconds: number): string => {
    if (isNaN(seconds)) return '0:00';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    const val = s < 10 ? `0${s}` : s;
    if (h > 0) {
      return `${h}:${m < 10 ? `0${m}` : m}:${val}`;
    }
    return `${m}:${val}`;
  };

  const handleDownloadToggle = async (book: BookManifestItem) => {
    const isOffline = offlineStatus[book.id];
    if (isOffline) {
      if (confirm(`Remove "${book.title}" from your offline device files?`)) {
        await downloadManager.removeDownload(book.id);
        triggerToast("Offline download removed.");
      }
    } else {
      const playlistPath = resolveUrl(book.playlistUrl);
      downloadManager.startDownload(book.id, playlistPath);
      triggerToast("Starting download...");
    }
  };

  // Get active playing book details
  const activeBook = books.find(b => b.id === activeBookId);

  return (
    <div className={`app-wrapper ${theme}`} style={{ display: 'flex', flexDirection: 'column', height: '100dvh', background: 'var(--bg-color)', color: 'var(--text-color)', position: 'relative', overflow: 'hidden' }}>
      
      {/* Hidden audio engine */}
      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onPause={() => {
          if (audioRef.current && audioRef.current.ended) return;
          setIsPlaying(false);
        }}
        onPlay={() => setIsPlaying(true)}
        onEnded={() => {
          if (sleepAtEndOfTrack) {
            if (audioRef.current) {
              audioRef.current.pause();
              setIsPlaying(false);
            }
            setSleepAtEndOfTrack(false);
            localStorage.removeItem('audio_sleep_at_end_of_track');
            triggerToast("Sleep timer finished. Playback paused.");
            return;
          }

          if (currentTrackIndex < tracks.length - 1) {
            setIsPlaying(true);
            setCurrentTrackIndex(prev => prev + 1);
          } else {
            setIsPlaying(false);
            triggerToast("End of book.");
          }
        }}
      />

      {/* Main Container */}
      <main style={{ flex: 1, overflowY: 'auto', paddingBottom: view !== 'onboarding' ? (activeBookId ? '140px' : '80px') : '40px' }} className="container">
        
        {/* Onboarding View */}
        {view === 'onboarding' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '80dvh', textAlign: 'center', padding: '0 1.5rem' }}>
            <h1 className="serif-text" style={{ fontSize: '3.5rem', marginBottom: '1.25rem', letterSpacing: '-0.02em' }}>ClaraNarratio</h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: '1.2rem', maxWidth: '500px', marginBottom: '3rem', lineHeight: '1.7' }}>
              Beautifully narrated public-domain classics. Step into a quiet listening library curated for deep focus.
            </p>
            <button className="btn btn-primary" onClick={() => setView('library')}>
              Explore Library
            </button>
          </div>
        )}

        {/* Library Tab (Curated grid + Local Shelf) */}
        {(view === 'library' || view === 'detail') && (
          <div style={{ paddingTop: '2.5rem' }}>
            {view === 'library' && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2.5rem' }}>
                  <h1 className="serif-text" style={{ fontSize: '2.5rem' }}>ClaraNarratio</h1>
                  <span style={{ fontSize: '0.9rem', padding: '0.4rem 0.8rem', borderRadius: 'var(--radius-full)', background: 'var(--surface-low)', fontWeight: 600 }}>
                    {isPro ? 'Premium Member' : 'Free Preview'}
                  </span>
                </div>

                {isDemoFallback && (
                  <div style={{ 
                    background: '#fef2f2', 
                    color: '#991b1b', 
                    padding: '1rem 1.25rem', 
                    borderRadius: 'var(--radius-md)', 
                    marginBottom: '2rem', 
                    fontSize: '0.95rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                  }}>
                    <span style={{ fontSize: '1.25rem' }}>⚠️</span>
                    <span><strong>Offline Mode:</strong> Showing demo fallback titles.</span>
                  </div>
                )}

                {/* Local Downloaded Shelf (transplanted Gutenberg & Siddhartha) */}
                <section style={{ marginBottom: '3.5rem' }}>
                  <h2 className="serif-text" style={{ fontSize: '1.8rem', marginBottom: '1.5rem' }}>Downloaded Books</h2>
                  {localBooks.length === 0 ? (
                    <div style={{ padding: '2rem', background: 'var(--surface-low)', borderRadius: 'var(--radius-lg)', textAlign: 'center', color: 'var(--text-secondary)' }}>
                      <p style={{ fontSize: '0.95rem', marginBottom: '1rem' }}>No offline texts downloaded yet.</p>
                      <button className="btn btn-secondary" onClick={() => setView('discover')}>
                        Search Gutenberg Books
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
                      {localBooks.map(book => (
                        <div key={book.id} className="no-line-card" style={{ display: 'flex', flexDirection: 'column' }}>
                          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.25rem', flex: 1 }}>
                            <div style={{ width: '60px', height: '90px', background: 'var(--surface-bright)', borderRadius: 'var(--radius-sm)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              {book.coverUrl ? (
                                <img src={resolveUrl(book.coverUrl)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : (
                                <span style={{ fontSize: '1.5rem' }}>📖</span>
                              )}
                            </div>
                            <div>
                              <h3 className="serif-text" style={{ fontSize: '1.2rem', marginBottom: '0.25rem', cursor: 'pointer' }} onClick={() => handleSelectBookForReading(book.id, book.title, book.authors?.[0]?.name || 'Unknown Author', book.corpusId || 'public_domain_gutendex')}>
                                {book.title}
                              </h3>
                              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{book.authors?.[0]?.name || 'Unknown Author'}</p>
                            </div>
                          </div>
                          
                          <div style={{ display: 'flex', gap: '0.75rem', marginTop: 'auto' }}>
                            <button className="btn btn-secondary" style={{ flex: 1, padding: '0.5rem' }} onClick={() => handleSelectBookForReading(book.id, book.title, book.authors?.[0]?.name || 'Unknown Author', book.corpusId || 'public_domain_gutendex')}>
                              Read
                            </button>
                            <button className="btn btn-primary" style={{ flex: 1, padding: '0.5rem' }} onClick={() => handleListenLocalBook(book)}>
                              Listen
                            </button>
                            <button 
                              onClick={() => handleDeleteBook(book.id, book.title)}
                              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0 0.5rem', fontSize: '1.1rem' }}
                              title="Delete Book"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </section>

                {/* Premium Curated Narrated Catalog Grid */}
                <section>
                  <h2 className="serif-text" style={{ fontSize: '1.8rem', marginBottom: '1.5rem' }}>Curated Audiobook Catalog</h2>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2.5rem' }}>
                    {books.map(book => {
                      const isOffline = offlineStatus[book.id];
                      return (
                        <div key={book.id} className="no-line-card" style={{ display: 'flex', flexDirection: 'column' }}>
                          <div 
                            onClick={() => handleBookClick(book)}
                            style={{ cursor: 'pointer', width: '100%', aspectRatio: '2/3', background: 'var(--surface-low)', borderRadius: 'var(--radius-md)', overflow: 'hidden', marginBottom: '1.25rem', position: 'relative' }}
                          >
                            <img 
                              src={resolveUrl(book.coverUrl)} 
                              alt={book.title} 
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                                const sibling = (e.target as HTMLImageElement).nextElementSibling;
                                if (sibling) (sibling as HTMLElement).style.display = 'flex';
                              }}
                            />
                            <div style={{ display: 'none', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '1.5rem', textAlign: 'center' }}>
                              <span style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📖</span>
                              <span className="serif-text" style={{ fontSize: '1.1rem', fontWeight: 600 }}>{book.title}</span>
                            </div>

                            {isOffline && (
                              <span style={{ position: 'absolute', top: '10px', right: '10px', background: 'rgba(68, 65, 196, 0.9)', color: '#ffffff', borderRadius: '50%', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }} title="Available Offline">
                                ✓
                              </span>
                            )}
                          </div>

                          <h3 className="serif-text" style={{ fontSize: '1.3rem', marginBottom: '0.2rem', cursor: 'pointer' }} onClick={() => handleBookClick(book)}>
                            {book.title}
                          </h3>
                          <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '1.25rem' }}>{book.author}</p>

                          {!book.hasAudio && (
                            <p style={{ fontSize: '0.85rem', color: '#b78103', fontStyle: 'italic', marginBottom: '1rem' }}>
                              {book.audioStatus === 'processing' ? 'Narration coming soon' : 'Narration unavailable'}
                            </p>
                          )}

                          <div style={{ marginTop: 'auto', display: 'flex', gap: '1rem' }}>
                            <button className="btn btn-secondary" style={{ flex: 1, padding: '0.6rem 1rem' }} onClick={() => handleBookClick(book)}>
                              Details
                            </button>
                            {book.hasAudio ? (
                              <button className="btn btn-primary" style={{ flex: 1, padding: '0.6rem 1rem' }} onClick={() => handleStartPlayback(book)}>
                                Listen
                              </button>
                            ) : (
                              <button 
                                className="btn btn-secondary" 
                                style={{ flex: 1, padding: '0.6rem 1rem' }} 
                                onClick={() => handleReadCuratedBook(book, false)}
                                disabled={downloadingTextIds.has(book.id)}
                              >
                                {downloadingTextIds.has(book.id) ? 'Loading...' : 'Read'}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              </>
            )}

            {/* Catalog Book Detail Sub-View */}
            {view === 'detail' && selectedBook && (
              <div>
                <button 
                  onClick={() => setView('library')} 
                  className="btn btn-secondary" 
                  style={{ padding: '0.5rem 1.2rem', fontSize: '0.9rem', marginBottom: '2.5rem' }}
                >
                  ← Back to Library
                </button>

                <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '3rem' }}>
                  <div style={{ flex: '1 1 300px', maxWidth: '360px' }}>
                    <div style={{ width: '100%', aspectRatio: '2/3', background: 'var(--surface-low)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', boxShadow: '0 12px 40px rgba(0,0,0,0.08)' }}>
                      <img 
                        src={resolveUrl(selectedBook.coverUrl)} 
                        alt={selectedBook.title} 
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </div>
                  </div>

                  <div style={{ flex: '2 1 400px', display: 'flex', flexDirection: 'column' }}>
                    <h1 className="serif-text" style={{ fontSize: '3rem', marginBottom: '0.5rem', lineHeight: '1.15' }}>{selectedBook.title}</h1>
                    <h2 style={{ fontSize: '1.25rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '2rem' }}>by {selectedBook.author}</h2>

                    <div style={{ background: 'var(--surface-low)', borderRadius: 'var(--radius-lg)', padding: '1.5rem', marginBottom: '2.5rem' }}>
                      <h3 className="serif-text" style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>Curator Note</h3>
                      <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: '1.6' }}>
                        Beautiful public-domain transcription, converted with natural sounding narration tracks. 
                        {selectedBook.isBookClub ? " This book features active book club discussion markers." : ""}
                      </p>
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', alignItems: 'center', width: '100%' }}>
                      {selectedBook.hasAudio ? (
                        <>
                          <button className="btn btn-primary" style={{ padding: '0.8rem 2.5rem' }} onClick={() => handleStartPlayback(selectedBook)}>
                            Start Listening
                          </button>

                          {/* Offline Switch */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: 'var(--surface-low)', padding: '0.6rem 1.2rem', borderRadius: 'var(--radius-full)' }}>
                            <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>Download Offline</span>
                            <button 
                              onClick={() => handleDownloadToggle(selectedBook)}
                              style={{
                                background: offlineStatus[selectedBook.id] ? 'var(--primary)' : 'var(--surface-bright)',
                                border: 'none',
                                color: offlineStatus[selectedBook.id] ? '#ffffff' : 'var(--text-color)',
                                width: '40px',
                                height: '24px',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                position: 'relative',
                                padding: '2px',
                                transition: 'background-color 0.2s'
                              }}
                            >
                              <div style={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                background: '#ffffff',
                                transform: offlineStatus[selectedBook.id] ? 'translateX(16px)' : 'translateX(0)',
                                transition: 'transform 0.2s',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.15)'
                              }} />
                            </button>
                          </div>
                        </>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', width: '100%' }}>
                          <p style={{ color: '#b78103', fontSize: '0.95rem', fontStyle: 'italic', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span>⚠️</span> {selectedBook.audioStatus === 'processing' ? 'Narration is currently processing and will be available soon.' : 'Narration is unavailable for this title.'}
                          </p>
                          <div style={{ display: 'flex', gap: '1rem', width: '100%', flexWrap: 'wrap' }}>
                            <button 
                              className="btn btn-primary" 
                              style={{ padding: '0.8rem 2.5rem', flex: 1, minWidth: '150px' }} 
                              onClick={() => handleReadCuratedBook(selectedBook, false)}
                              disabled={downloadingTextIds.has(selectedBook.id)}
                            >
                              {downloadingTextIds.has(selectedBook.id) ? 'Downloading...' : 'Read Book'}
                            </button>
                            <button 
                              className="btn btn-secondary" 
                              style={{ padding: '0.8rem 2.5rem', flex: 1, minWidth: '150px', background: 'var(--surface-low)' }} 
                              onClick={() => handleReadCuratedBook(selectedBook, true)}
                              disabled={downloadingTextIds.has(selectedBook.id)}
                            >
                              Play via TTS 🗣️
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Premium download progress */}
                    {downloads[selectedBook.id]?.state === 'downloading' && (
                      <div style={{ marginTop: '1.5rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '0.4rem', fontWeight: 600 }}>
                          <span>Downloading audio chunks...</span>
                          <span>{downloads[selectedBook.id].progress}%</span>
                        </div>
                        <div style={{ width: '100%', height: '6px', background: 'var(--surface-bright)', borderRadius: '3px', overflow: 'hidden' }}>
                          <div style={{ width: `${downloads[selectedBook.id].progress}%`, height: '100%', background: 'var(--primary)' }} />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Discover Tab (Gutenberg search & download) */}
        {view === 'discover' && (
          <div style={{ paddingTop: '2.5rem' }}>
            <h1 className="serif-text" style={{ fontSize: '2.5rem', marginBottom: '1.5rem' }}>Discover Classics</h1>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>Search and download from the Project Gutenberg library of public-domain books.</p>
            
            {/* Search Input bar */}
            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '2rem' }} className="no-line-card">
              <input 
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder="Search by title or author (e.g. Frankenstein, Austen)..."
                style={{
                  flex: 1,
                  border: 'none',
                  background: 'var(--surface-low)',
                  color: 'var(--text-color)',
                  padding: '0.8rem 1.2rem',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '1rem',
                  outline: 'none'
                }}
              />
              <button 
                className="btn btn-primary"
                onClick={handleSearch}
                disabled={searching}
              >
                {searching ? 'Searching...' : 'Search'}
              </button>
            </div>

            {searchError && (
              <div style={{ color: '#d32f2f', marginBottom: '2rem', padding: '1rem', background: '#ffebee', borderRadius: 'var(--radius-md)' }}>
                {searchError}
              </div>
            )}

            {/* Results Shelf */}
            {searchResults.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '2rem' }}>
                {searchResults.map(book => {
                  const isDownloaded = localBooks.some(l => l.id === book.id.toString());
                  const isDownloading = downloadingBookIds.has(book.id.toString());
                  return (
                    <div key={book.id} className="no-line-card" style={{ display: 'flex', gap: '1rem' }}>
                      <div style={{ width: '80px', height: '120px', background: 'var(--surface-bright)', borderRadius: 'var(--radius-sm)', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {book.formats['image/jpeg'] ? (
                          <img src={book.formats['image/jpeg']} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <span style={{ fontSize: '1.8rem' }}>📖</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                        <h3 className="serif-text" style={{ fontSize: '1.15rem', marginBottom: '0.25rem', lineHeight: '1.25' }}>{book.title}</h3>
                        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>{book.authors.map(a => a.name).join(', ')}</p>
                        
                        <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'flex-end' }}>
                          {isDownloaded ? (
                            <button className="btn btn-secondary" style={{ padding: '0.4rem 1rem', fontSize: '0.8rem' }} onClick={() => handleSelectBookForReading(book.id.toString(), book.title, book.authors[0]?.name || 'Unknown', 'public_domain_gutendex')}>
                              Read ✓
                            </button>
                          ) : (
                            <button 
                              className="btn btn-primary" 
                              style={{ padding: '0.4rem 1rem', fontSize: '0.8rem' }} 
                              onClick={() => handleDownloadGutenberg(book)}
                              disabled={isDownloading}
                            >
                              {isDownloading ? 'Downloading...' : 'Download'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              !searching && searchQuery !== '' && (
                <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                  No results found. Try other keywords.
                </div>
              )
            )}
          </div>
        )}

        {/* Reader Tab */}
        {view === 'reader' && (
          <div style={{ paddingTop: '1.5rem', maxWidth: '720px', margin: '0 auto' }}>
            {readerBook ? (
              <>
                {/* Book info & Chapter Nav Header */}
                <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
                  <h2 className="serif-text" style={{ fontSize: '1.75rem', marginBottom: '0.25rem' }}>{readerBook.title}</h2>
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>by {readerBook.author}</p>
                </div>

                {/* Chapter Select controllers */}
                {(() => {
                  const corpus = CorpusService.get(readerBook.corpusId);
                  const sections = corpus ? corpus.getSections() : [];
                  const section = sections.find(s => s.id === readerBook.id || (readerBook.corpusId === 'siddhartha' && s.id === 'main'));
                  const maxChapters = section?.chapterCount || 0;
                  const chapterTitles = section?.chapterTitles;
                  const nav = getReaderNavigationLabels(
                    readerBook.id,
                    readerBook.corpusId,
                    currentChapter,
                    maxChapters,
                    chapterTitles
                  );
                  return (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                      <button 
                        className="btn btn-secondary" 
                        style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }}
                        disabled={currentChapter === 1}
                        aria-label={nav.prevAria}
                        title={nav.prevAria}
                        onClick={() => {
                          if (isSpeaking) tts.stop('chapter_change');
                          setVerseData(null);
                          setCurrentChapter(c => c - 1);
                          setPageIndex(0);
                        }}
                      >
                        {nav.prevLabel}
                      </button>
                      <span style={{ fontStyle: 'italic', fontSize: '1rem', fontWeight: 600, textAlign: 'center', padding: '0 0.5rem' }}>
                        {nav.centerLabel}
                      </span>
                      <button 
                        className="btn btn-secondary" 
                        style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }}
                        aria-label={nav.nextAria}
                        title={nav.nextAria}
                        onClick={() => {
                          if (isSpeaking) tts.stop('chapter_change');
                          const success = advancePageOrChapter('manual');
                          if (!success) triggerToast("End of book.");
                        }}
                      >
                        {nav.nextLabel}
                      </button>
                    </div>
                  );
                })()}

                {/* Central Reading area with safe click zones */}
                <div 
                  onClick={handlePageClick}
                  style={{
                    flex: 1,
                    padding: '2.5rem 2rem',
                    borderRadius: 'var(--radius-lg)',
                    background: 'var(--surface-lowest)',
                    boxShadow: '0 8px 32px rgba(27,28,25,0.02)',
                    cursor: 'pointer',
                    minHeight: '45vh',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    position: 'relative'
                  }}
                >
                  {isPaginating || !verseData ? (
                    <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                      <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⏳</div>
                      <div>Paginating text...</div>
                    </div>
                  ) : pages[pageIndex] && pages[pageIndex].length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                      {pages[pageIndex].map((key) => (
                        <p 
                          key={key} 
                          style={{ 
                            fontSize: '1.15rem', 
                            lineHeight: '1.8', 
                            textIndent: '1em', 
                            textAlign: 'justify',
                            color: 'var(--text-color)'
                          }}
                        >
                          {verseData[key]}
                        </p>
                      ))}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>No content.</div>
                  )}
                </div>

                {/* Left/Right click instructions */}
                <p style={{ textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.75rem' }}>
                  Tap left 35% to go back, right 65% to advance
                </p>

                {/* Docked mini TTS synthesis controller bar */}
                <div 
                  className="glass" 
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.8rem 1.5rem',
                    borderRadius: 'var(--radius-lg)',
                    marginTop: '1.5rem',
                    background: 'var(--surface-low)',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.02)'
                  }}
                >
                  <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
                    Page {pageIndex + 1} of {pages.length || 1}
                  </span>
                  
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                    <button 
                      className="btn btn-secondary" 
                      style={{ padding: '0.4rem 1rem', fontSize: '0.85rem' }}
                      onClick={() => {
                        if (!verseData || pages.length === 0 || !pages[pageIndex]) return;
                        // Pause narrated audio player
                        if (isPlaying && audioRef.current) {
                          audioRef.current.pause();
                          setIsPlaying(false);
                        }
                        tts.restart(pages[pageIndex].map(v => verseData[v] || ''));
                      }}
                    >
                      Restart Page
                    </button>
                    
                    <button 
                      className="btn btn-primary" 
                      style={{ padding: '0.4rem 1.5rem', fontSize: '0.85rem', minWidth: '90px' }}
                      onClick={handleTtsToggle}
                    >
                      {isSpeaking ? (isBuffering ? '⏳' : 'Pause ⏸️') : 'Listen 🎧'}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div style={{ padding: '4rem 2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📖</div>
                <p style={{ fontSize: '1.1rem', marginBottom: '1.5rem' }}>No book open in reader.</p>
                <button className="btn btn-primary" onClick={() => setView('library')}>
                  Select a Book to Read
                </button>
              </div>
            )}
          </div>
        )}

        {/* Player Tab (detailed premium narrated player) */}
        {view === 'player' && (
          <div style={{ paddingTop: '2.5rem', maxWidth: '480px', margin: '0 auto' }}>
            <h1 className="serif-text" style={{ fontSize: '2.5rem', marginBottom: '2rem', textAlign: 'center' }}>Audio Player</h1>
            
            {activeBookId && activeBook ? (
              <div className="no-line-card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <div style={{ width: '220px', aspectRatio: '2/3', borderRadius: 'var(--radius-lg)', overflow: 'hidden', background: 'var(--surface-low)', boxShadow: '0 12px 36px rgba(0,0,0,0.1)', marginBottom: '2rem' }}>
                  <img src={resolveUrl(activeBook.coverUrl)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>

                <h2 className="serif-text" style={{ fontSize: '1.8rem', marginBottom: '0.4rem' }}>{activeBook.title}</h2>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', marginBottom: '1.5rem' }}>by {activeBook.author}</p>
                
                <p style={{ fontSize: '0.85rem', color: audioError ? '#d32f2f' : 'var(--text-secondary)', marginBottom: '2rem' }}>
                  {audioError || `Track ${currentTrackIndex + 1} of ${tracks.length}`}
                </p>

                {/* Scrubber timeline */}
                <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem' }}>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontFamily: 'monospace', width: '45px' }}>
                    {formatTime(currentTime)}
                  </span>
                  <input 
                    type="range"
                    min={0}
                    max={duration || 100}
                    value={currentTime}
                    onChange={(e) => handleSeek(parseFloat(e.target.value))}
                    style={{ flex: 1, accentColor: 'var(--primary)', height: '4px', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontFamily: 'monospace', width: '45px', textAlign: 'right' }}>
                    {formatTime(duration)}
                  </span>
                </div>

                {/* Controls */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '2rem', marginBottom: '2rem' }}>
                  <button onClick={() => skipSeconds(-15)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.5rem' }} title="Rewind 15s">⏪</button>
                  <button onClick={prevTrack} disabled={currentTrackIndex === 0} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.5rem', opacity: currentTrackIndex === 0 ? 0.3 : 1 }} title="Previous track">⏮️</button>
                  
                  <button 
                    onClick={togglePlayPause}
                    style={{
                      background: 'var(--primary)',
                      color: '#ffffff',
                      border: 'none',
                      width: '64px',
                      height: '64px',
                      borderRadius: '50%',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      boxShadow: '0 4px 16px rgba(68, 65, 196, 0.25)'
                    }}
                  >
                    {audioLoading ? '⏳' : isPlaying ? '⏸️' : '▶️'}
                  </button>

                  <button onClick={nextTrack} disabled={currentTrackIndex === tracks.length - 1} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.5rem', opacity: currentTrackIndex === tracks.length - 1 ? 0.3 : 1 }} title="Next track">⏭️</button>
                  <button onClick={() => skipSeconds(15)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.5rem' }} title="Forward 15s">⏩</button>
                </div>

                {/* Speed & Sleep timer */}
                <div style={{ display: 'flex', gap: '1rem', width: '100%', justifyContent: 'center' }}>
                  <select 
                    value={playbackSpeed} 
                    onChange={(e) => setPlaybackSpeed(parseFloat(e.target.value))}
                    style={{ background: 'var(--surface-low)', color: 'var(--text-color)', border: 'none', padding: '0.5rem 1rem', borderRadius: 'var(--radius-full)', fontSize: '0.85rem', cursor: 'pointer', fontWeight: 600 }}
                  >
                    <option value="0.75">0.75x Speed</option>
                    <option value="1.0">1.0x Speed</option>
                    <option value="1.25">1.25x Speed</option>
                    <option value="1.5">1.5x Speed</option>
                    <option value="2.0">2.0x Speed</option>
                  </select>

                  <button 
                    onClick={() => setShowSleepPicker(true)}
                    style={{ background: (sleepTimerEndAt || sleepAtEndOfTrack) ? 'rgba(68, 65, 196, 0.15)' : 'var(--surface-low)', color: (sleepTimerEndAt || sleepAtEndOfTrack) ? 'var(--primary)' : 'var(--text-color)', border: 'none', padding: '0.5rem 1rem', borderRadius: 'var(--radius-full)', fontSize: '0.85rem', cursor: 'pointer', fontWeight: 600 }}
                  >
                    ⏰ {sleepAtEndOfTrack ? 'End of Track' : (sleepTimerEndAt ? `${Math.max(0, Math.ceil((sleepTimerEndAt - Date.now()) / 60000))}m` : 'Sleep Timer')}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ padding: '4rem 2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎧</div>
                <p style={{ fontSize: '1.1rem', marginBottom: '1.5rem' }}>No premium audiobook playlist active.</p>
                <button className="btn btn-primary" onClick={() => setView('library')}>
                  Browse Narrated Catalog
                </button>
              </div>
            )}
          </div>
        )}

        {/* Settings Tab (theme toggle + Web Speech synthesizer settings) */}
        {view === 'settings' && (
          <div style={{ paddingTop: '2.5rem', maxWidth: '600px', margin: '0 auto' }}>
            <h1 className="serif-text" style={{ fontSize: '2.5rem', marginBottom: '2rem' }}>Settings</h1>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              
              {/* Theme Settings Card */}
              <div className="no-line-card">
                <h3 className="serif-text" style={{ fontSize: '1.4rem', marginBottom: '0.5rem' }}>Appearance</h3>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1.25rem' }}>Adjust visual styling preferences.</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Dark Mode Theme</span>
                  <button 
                    onClick={() => setTheme(prev => prev === 'light' ? 'dark' : 'light')}
                    className="btn btn-secondary"
                    style={{ padding: '0.5rem 1.2rem', fontSize: '0.9rem' }}
                  >
                    {theme === 'light' ? 'Enable' : 'Disable'}
                  </button>
                </div>
              </div>

              {/* Web Speech TTS Engine configuration */}
              <div className="no-line-card" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                <div>
                  <h3 className="serif-text" style={{ fontSize: '1.4rem', marginBottom: '0.25rem' }}>Text-To-Speech (TTS)</h3>
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Configure browser-native narration settings.</p>
                </div>

                {/* Voice selection */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>Narration Voice</label>
                  {voices.length === 0 ? (
                    <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>No SpeechSynthesis voices detected. Using system default.</p>
                  ) : (
                    <select
                      value={voiceSettings.free.voiceURI || ''}
                      onChange={e => handleVoiceChange(e.target.value)}
                      style={{ background: 'var(--surface-low)', color: 'var(--text-color)', border: 'none', padding: '0.7rem 1rem', borderRadius: 'var(--radius-md)', fontSize: '0.95rem', width: '100%', outline: 'none' }}
                    >
                      <option value="">System Default Voice</option>
                      {[...voices].sort((a,b) => a.name.localeCompare(b.name)).map(v => (
                        <option key={v.voiceURI} value={v.voiceURI}>{v.name} ({v.lang})</option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Reading speed slider */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', fontWeight: 600 }}>
                    <label>Narration Speed (Rate)</label>
                    <span>{voiceSettings.free.rate.toFixed(1)}x</span>
                  </div>
                  <input 
                    type="range"
                    min={0.5}
                    max={2.0}
                    step={0.1}
                    value={voiceSettings.free.rate}
                    onChange={e => handleRateChange(parseFloat(e.target.value))}
                    style={{ accentColor: 'var(--primary)', cursor: 'pointer' }}
                  />
                </div>

                {/* Voice Pitch slider */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', fontWeight: 600 }}>
                    <label>Pitch</label>
                    <span>{voiceSettings.free.pitch.toFixed(1)}</span>
                  </div>
                  <input 
                    type="range"
                    min={0.5}
                    max={2.0}
                    step={0.1}
                    value={voiceSettings.free.pitch}
                    onChange={e => handlePitchChange(parseFloat(e.target.value))}
                    style={{ accentColor: 'var(--primary)', cursor: 'pointer' }}
                  />
                </div>

                {/* Auto Page Flip checkbox */}
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                  <input 
                    type="checkbox"
                    checked={!!voiceSettings.autoPageFlip}
                    onChange={e => handleAutoPageFlipChange(e.target.checked)}
                    style={{ accentColor: 'var(--primary)' }}
                  />
                  Auto-flip pages during TTS reading
                </label>
              </div>

              {/* Build Diagnostics Card */}
              <div className="no-line-card">
                <h3 className="serif-text" style={{ fontSize: '1.4rem', marginBottom: '0.5rem' }}>Diagnostics</h3>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1.25rem', lineHeight: '1.6' }}>
                  Technical diagnostics for support inquiries.
                </p>
                <div style={{ fontSize: '0.8rem', fontFamily: 'monospace', color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: '1.5' }}>
                  <div>ClaraNarratio PWA v{__BUILD_INFO__.version}</div>
                  <div>Build: {__BUILD_INFO__.buildId}</div>
                  <div>Date: {new Date(__BUILD_INFO__.date).toLocaleString()}</div>
                  <div>API Target: proxy target (https://corpora.wikiboox.com)</div>
                </div>
                <button 
                  className="btn btn-secondary" 
                  style={{ width: '100%', padding: '0.6rem 1rem', fontSize: '0.85rem' }}
                  onClick={() => {
                    const diag = `ClaraNarratio v${__BUILD_INFO__.version} | Build: ${__BUILD_INFO__.buildId} | Date: ${__BUILD_INFO__.date}`;
                    navigator.clipboard.writeText(diag);
                    triggerToast("Diagnostics copied to clipboard!");
                  }}
                >
                  Copy Diagnostics
                </button>
              </div>

              {/* Support Assist & Trust links */}
              <div style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: '1rem' }}>
                <p style={{ marginBottom: '1rem' }}>Need help? Support: <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>support@claranarratio.com</span></p>
                <div style={{ display: 'flex', gap: '1.5rem', justifySelf: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <a href="https://claranarratio.com/terms.html" target="_blank" rel="noreferrer" style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>Terms</a>
                  <a href="https://claranarratio.com/privacy.html" target="_blank" rel="noreferrer" style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>Privacy</a>
                  <a href="https://claranarratio.com/refund.html" target="_blank" rel="noreferrer" style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>Refunds</a>
                </div>
              </div>
            </div>
          </div>
        )}

      </main>

      {/* Mini-Player Strip (renders on other tabs when premium narrated book is playing) */}
      {view !== 'onboarding' && view !== 'player' && activeBookId && activeBook && (
        <div 
          className="glass" 
          onClick={() => setView('player')}
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: '64px',
            height: '60px',
            zIndex: 140,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 1.5rem',
            boxShadow: '0 -4px 12px rgba(0,0,0,0.03)',
            cursor: 'pointer',
            borderTop: 'none'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: 'var(--radius-sm)', overflow: 'hidden', background: 'var(--surface-bright)', flexShrink: 0 }}>
              <img src={resolveUrl(activeBook.coverUrl)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            <div style={{ maxWidth: '180px', overflow: 'hidden' }}>
              <h4 className="serif-text" style={{ fontSize: '0.9rem', fontWeight: 600, textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                {activeBook.title}
              </h4>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                Track {currentTrackIndex + 1}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }} onClick={e => e.stopPropagation()}>
            <button 
              onClick={togglePlayPause} 
              style={{
                background: 'var(--primary)',
                color: '#ffffff',
                border: 'none',
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.9rem',
                cursor: 'pointer'
              }}
            >
              {audioLoading ? '⏳' : isPlaying ? '⏸️' : '▶️'}
            </button>
            <button 
              onClick={() => {
                if (audioRef.current) {
                  audioRef.current.pause();
                }
                setIsPlaying(false);
                setActiveBookId(null);
              }}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1rem', color: 'var(--text-secondary)' }}
              title="Close Player"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Persistent Bottom 5-Tab Navigation Bar */}
      {view !== 'onboarding' && (
        <nav 
          className="glass" 
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            height: '64px',
            display: 'flex',
            justifyContent: 'space-around',
            alignItems: 'center',
            zIndex: 150,
            boxShadow: '0 -4px 20px rgba(0,0,0,0.02)',
            paddingBottom: 'safe-area-inset-bottom',
            borderTop: 'none'
          }}
        >
          {/* Tab: Library */}
          <button 
            onClick={() => setView('library')}
            style={{
              background: 'transparent',
              border: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              cursor: 'pointer',
              color: (view === 'library' || view === 'detail') ? 'var(--primary)' : 'var(--text-secondary)',
              fontSize: '0.75rem',
              fontWeight: 600,
              gap: '2px',
              position: 'relative'
            }}
          >
            <span style={{ fontSize: '1.25rem' }}>📚</span>
            <span>Library</span>
            {(view === 'library' || view === 'detail') && (
              <span style={{ position: 'absolute', bottom: '-8px', width: '4px', height: '4px', borderRadius: '50%', background: 'var(--primary)' }} />
            )}
          </button>

          {/* Tab: Discover */}
          <button 
            onClick={() => setView('discover')}
            style={{
              background: 'transparent',
              border: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              cursor: 'pointer',
              color: view === 'discover' ? 'var(--primary)' : 'var(--text-secondary)',
              fontSize: '0.75rem',
              fontWeight: 600,
              gap: '2px',
              position: 'relative'
            }}
          >
            <span style={{ fontSize: '1.25rem' }}>🔍</span>
            <span>Discover</span>
            {view === 'discover' && (
              <span style={{ position: 'absolute', bottom: '-8px', width: '4px', height: '4px', borderRadius: '50%', background: 'var(--primary)' }} />
            )}
          </button>

          {/* Tab: Reader */}
          <button 
            onClick={() => setView('reader')}
            style={{
              background: 'transparent',
              border: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              cursor: 'pointer',
              color: view === 'reader' ? 'var(--primary)' : 'var(--text-secondary)',
              fontSize: '0.75rem',
              fontWeight: 600,
              gap: '2px',
              position: 'relative'
            }}
          >
            <span style={{ fontSize: '1.25rem' }}>📖</span>
            <span>Reader</span>
            {view === 'reader' && (
              <span style={{ position: 'absolute', bottom: '-8px', width: '4px', height: '4px', borderRadius: '50%', background: 'var(--primary)' }} />
            )}
          </button>

          {/* Tab: Player */}
          <button 
            onClick={() => setView('player')}
            style={{
              background: 'transparent',
              border: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              cursor: 'pointer',
              color: view === 'player' ? 'var(--primary)' : 'var(--text-secondary)',
              fontSize: '0.75rem',
              fontWeight: 600,
              gap: '2px',
              position: 'relative'
            }}
          >
            <span style={{ fontSize: '1.25rem' }}>🎧</span>
            <span>Player</span>
            {view === 'player' && (
              <span style={{ position: 'absolute', bottom: '-8px', width: '4px', height: '4px', borderRadius: '50%', background: 'var(--primary)' }} />
            )}
          </button>

          {/* Tab: Settings */}
          <button 
            onClick={() => setView('settings')}
            style={{
              background: 'transparent',
              border: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              cursor: 'pointer',
              color: view === 'settings' ? 'var(--primary)' : 'var(--text-secondary)',
              fontSize: '0.75rem',
              fontWeight: 600,
              gap: '2px',
              position: 'relative'
            }}
          >
            <span style={{ fontSize: '1.25rem' }}>⚙️</span>
            <span>Settings</span>
            {view === 'settings' && (
              <span style={{ position: 'absolute', bottom: '-8px', width: '4px', height: '4px', borderRadius: '50%', background: 'var(--primary)' }} />
            )}
          </button>
        </nav>
      )}

      {/* Sleep Timer Picker Modal */}
      {showSleepPicker && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="no-line-card" style={{ width: '320px', padding: '2rem', textAlign: 'center' }}>
            <h3 className="serif-text" style={{ fontSize: '1.5rem', marginBottom: '1.5rem' }}>Sleep Timer</h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setSleepAtEndOfTrack(true);
                  localStorage.setItem('audio_sleep_at_end_of_track', 'true');
                  setSleepTimerEndAt(null);
                  localStorage.removeItem('audio_sleep_timer_endAt');
                  setShowSleepPicker(false);
                  triggerToast("Sleep timer set for end of track.");
                }}
              >
                End of Track
              </button>
              {[15, 30, 45, 60].map(mins => (
                <button 
                  key={mins}
                  className="btn btn-secondary"
                  onClick={() => {
                    const endAt = Date.now() + mins * 60 * 1000;
                    setSleepTimerEndAt(endAt);
                    localStorage.setItem('audio_sleep_timer_endAt', endAt.toString());
                    setSleepAtEndOfTrack(false);
                    localStorage.removeItem('audio_sleep_at_end_of_track');
                    setShowSleepPicker(false);
                    triggerToast(`Sleep timer set for ${mins} minutes.`);
                  }}
                >
                  {mins} Minutes
                </button>
              ))}
              {(sleepTimerEndAt || sleepAtEndOfTrack) && (
                <button 
                  className="btn" 
                  style={{ background: '#fce8e6', color: '#a51d24' }}
                  onClick={() => {
                    setSleepTimerEndAt(null);
                    localStorage.removeItem('audio_sleep_timer_endAt');
                    setSleepAtEndOfTrack(false);
                    localStorage.removeItem('audio_sleep_at_end_of_track');
                    setShowSleepPicker(false);
                    triggerToast("Sleep timer cancelled.");
                  }}
                >
                  Cancel Timer
                </button>
              )}
            </div>

            <button className="btn btn-secondary" style={{ width: '100%' }} onClick={() => setShowSleepPicker(false)}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* Resume Playback Prompt Modal */}
      {showResumePrompt && resumeCandidate && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="no-line-card" style={{ width: '360px', padding: '2.5rem', textAlign: 'center' }}>
            <h3 className="serif-text" style={{ fontSize: '1.75rem', marginBottom: '1rem' }}>Resume Playback?</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', marginBottom: '2rem', lineHeight: '1.6' }}>
              We found your saved progress. Would you like to resume listening from Track {resumeCandidate.trackIndex + 1} at {formatTime(resumeCandidate.position)}?
            </p>
            
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={declineResume}>
                Start Over
              </button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={applyResume}>
                Resume
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Playback Error Fallback Modal */}
      {showPlaybackErrorModal && playbackErrorBook && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="no-line-card" style={{ width: '400px', padding: '2.5rem', textAlign: 'center' }}>
            <span style={{ fontSize: '3rem', marginBottom: '1rem', display: 'block' }}>🔇</span>
            <h3 className="serif-text" style={{ fontSize: '1.75rem', marginBottom: '1rem' }}>Narration Unavailable</h3>
            
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', marginBottom: '2rem', lineHeight: '1.6' }}>
              Narration is not available for this title yet. You can still read it or use browser narration.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <button 
                className="btn btn-primary" 
                onClick={() => {
                  setShowPlaybackErrorModal(false);
                  handleReadCuratedBook(playbackErrorBook, false);
                }}
              >
                Read Book
              </button>
              <button 
                className="btn btn-secondary" 
                onClick={() => {
                  setShowPlaybackErrorModal(false);
                  handleReadCuratedBook(playbackErrorBook, true);
                }}
              >
                Play via TTS 🗣️
              </button>
              <button 
                className="btn btn-secondary" 
                style={{ background: 'var(--surface-low)' }}
                onClick={() => {
                  setShowPlaybackErrorModal(false);
                  setPlaybackErrorBook(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Non-Member Limit Upsell Modal */}
      {showUpsell && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="no-line-card" style={{ width: '440px', padding: '3rem', textAlign: 'center' }}>
            <span style={{ fontSize: '3rem', marginBottom: '1rem', display: 'block' }}>🔒</span>
            <h3 className="serif-text" style={{ fontSize: '2rem', marginBottom: '1rem' }}>Premium Feature</h3>
            
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', marginBottom: '2rem', lineHeight: '1.6' }}>
              You've enjoyed 5 minutes of premium narration. Unlock the entire audiobook catalog, offline downloads, and community book club discussions.
            </p>

            <div style={{ background: 'var(--surface-low)', padding: '1rem 1.5rem', borderRadius: 'var(--radius-md)', marginBottom: '2rem', fontSize: '0.9rem', textAlign: 'left' }}>
              <p style={{ fontWeight: 600, color: 'var(--primary)', marginBottom: '0.2rem' }}>ClaraNarratio Membership Includes:</p>
              <p style={{ margin: '0.2rem 0' }}>• Unlimited streaming of all classic books</p>
              <p style={{ margin: '0.2rem 0' }}>• High fidelity offline audiobook downloads</p>
              <p style={{ margin: '0.2rem 0' }}>• Save positions & custom reading speeds</p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <a href="https://claranarratio.com/#pricing" target="_blank" rel="noreferrer" className="btn btn-primary" style={{ textDecoration: 'none' }}>
                Join ClaraNarratio for $4.99/mo
              </a>
              <button className="btn btn-secondary" onClick={() => setShowUpsell(false)}>
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global Toast Alert */}
      {toast && (
        <div 
          style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 300,
            background: 'var(--text-color)',
            color: 'var(--bg-color)',
            padding: '0.75rem 1.75rem',
            borderRadius: 'var(--radius-full)',
            fontSize: '0.9rem',
            fontWeight: 600,
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            pointerEvents: 'none'
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}
